<script setup>
// Inspector do nó "Atualizar contato": marca quais atributos do contato a IA
// pode gravar. O servidor só aceita esses campos e valida pelo tipo.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AttributeAPI from 'dashboard/api/attributes';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const definitions = ref([]);
const loading = ref(true);
const selected = computed(() => props.data.allowedFields || []);

const toggle = key => {
  const next = selected.value.includes(key)
    ? selected.value.filter(k => k !== key)
    : [...selected.value, key];
  emit('update', { allowedFields: next });
};

onMounted(async () => {
  try {
    const { data } = await AttributeAPI.getAttributesByModel();
    const list = Array.isArray(data) ? data : data?.payload || [];
    definitions.value = list.filter(
      d => d.attribute_model === 'contact_attribute'
    );
  } catch {
    definitions.value = [];
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-lucide-user-pen text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.CRM.TITLE_UPDATE_CONTACT') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <div class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
      <p class="text-sm text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.tool_update_contact.DESC') }}
      </p>
    </div>

    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
      </span>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#00897b]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <p class="mb-1 text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.CRM.FIELDS') }}
    </p>
    <p
      v-if="!loading && !definitions.length"
      class="rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
    >
      {{ t('AI_AGENTS.FLOW.CRM.NO_FIELDS') }}
    </p>
    <label
      v-for="d in definitions"
      :key="d.attribute_key"
      class="flex items-center gap-2 py-1 text-sm text-n-slate-12"
    >
      <input
        type="checkbox"
        class="accent-[#00897b]"
        :checked="selected.includes(d.attribute_key)"
        @change="toggle(d.attribute_key)"
      />
      {{ d.attribute_display_name }}
      <span class="text-xs text-n-slate-10">{{ d.attribute_key }}</span>
    </label>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
